<!-- MemberSheetImport.vue -->
<!-- Importação das planilhas de membros, dentro do painel de
     ManagedAccountOnboarding.vue.

     Não fala com POST /managed-accounts/batch: produz os MESMOS itens que os
     modos "Nova conta" e "Vincular existente" produzem e devolve pelo evento
     `add`. Quem envia continua sendo o pai, pelo botão "Concluir" — assim
     limite de plano, cards de erro e consentimento valem igual nos três
     caminhos, em vez de a importação virar um segundo pipeline até a API.

     A única chamada de rede daqui é o precheck, que não cria nada: ele diz
     quais e-mails/usuários já existem para a conferência apontar a linha antes
     do envio. É indispensável porque o lote é tudo-ou-nada — sem ele, um
     e-mail repetido derruba a planilha inteira depois de tudo pronto. -->
<script setup>
import { computed, reactive, ref } from "vue";
import * as managedAccountService from "@/services/managedAccountService";
import {
  IMPORT_KINDS,
  SheetImportError,
  analyzeImportRows,
  buildImportedItem,
  getImportKind,
  groupNameKey,
  readMemberSheet,
  summarizeImportGroups,
} from "@/utils/memberSheetImport";
import { passwordCriteria, isPasswordValid } from "@/utils/memberValidation";

const props = defineProps({
  // Itens já na "Lista para confirmar": entram na checagem de duplicidade,
  // senão a planilha repete alguém que acabou de ser adicionado à mão.
  stagedItems: { type: Array, default: () => [] },
  remainingSlots: { type: Number, default: 0 },
  // Vagas de "pessoas em grupos" do plano, já descontado o que está na lista
  // para confirmar. Cada linha com turma consome uma; o lote recusa tudo se
  // não couber, então a conferência avisa antes.
  remainingGroupSlots: { type: Number, default: 0 },
});

const emit = defineEmits(["add", "expand"]);

// 'escolher' | 'conferir'
const step = ref("escolher");

const MAX_FILE_BYTES = 2 * 1024 * 1024;

function emptySlot() {
  return {
    file: null,
    rows: [],
    templateVersion: null,
    skippedRows: 0,
    password: "",
    showPassword: false,
    error: "",
    isReading: false,
  };
}

const slots = reactive({
  alunos: emptySlot(),
  professores: emptySlot(),
});

const analyzedRows = ref([]);
const existingEmails = ref(new Set());
const existingUserNames = ref(new Set());
const existingGroupNames = ref(new Set());
const reservedGroupNames = ref(new Set());
const isChecking = ref(false);
const precheckError = ref("");
const filter = ref("todas");
const overflowAccepted = ref(false);

const kinds = IMPORT_KINDS;

// ─── Passo 1: leitura dos arquivos ───────────────────────────────────────────

const loadedKinds = computed(() =>
  kinds.filter((k) => slots[k.key].rows.length > 0)
);

const totalParsedRows = computed(() =>
  loadedKinds.value.reduce((sum, k) => sum + slots[k.key].rows.length, 0)
);

const outdatedTemplateKinds = computed(() =>
  loadedKinds.value.filter(
    (k) =>
      slots[k.key].templateVersion &&
      slots[k.key].templateVersion !== k.templateVersion
  )
);

const missingPasswordKinds = computed(() =>
  loadedKinds.value.filter((k) => !isPasswordValid(slots[k.key].password))
);

const canReview = computed(
  () =>
    loadedKinds.value.length > 0 &&
    missingPasswordKinds.value.length === 0 &&
    !isChecking.value
);

async function handleFile(kindKey, file) {
  const slot = slots[kindKey];
  slot.error = "";

  if (!file) return;

  if (!/\.xlsx$/i.test(file.name)) {
    slot.error = "Envie um arquivo .xlsx. Planilhas .xls e .csv não são aceitas.";
    return;
  }
  if (file.size > MAX_FILE_BYTES) {
    slot.error = "O arquivo passa de 2 MB. Use o modelo, sem imagens nem abas extras.";
    return;
  }

  slot.isReading = true;
  try {
    const { rows, templateVersion, skippedRows } = await readMemberSheet(file, kindKey);
    slot.file = file;
    slot.rows = rows;
    slot.templateVersion = templateVersion;
    slot.skippedRows = skippedRows;
  } catch (error) {
    slot.file = null;
    slot.rows = [];
    slot.error =
      error instanceof SheetImportError
        ? error.message
        : "Não foi possível ler a planilha. Confirme que o arquivo veio do modelo.";
  } finally {
    slot.isReading = false;
  }
}

function onDrop(kindKey, event) {
  const file = event.dataTransfer?.files?.[0];
  handleFile(kindKey, file);
}

function onPick(kindKey, event) {
  const file = event.target.files?.[0];
  handleFile(kindKey, file);
  // Permite reenviar o mesmo arquivo depois de corrigi-lo no Excel.
  event.target.value = "";
}

function clearSlot(kindKey) {
  Object.assign(slots[kindKey], emptySlot());
}

// ─── Passo 2: conferência ────────────────────────────────────────────────────

function collectRows() {
  return loadedKinds.value.flatMap((k) => slots[k.key].rows);
}

function reanalyze() {
  analyzedRows.value = analyzeImportRows(analyzedRows.value, {
    stagedItems: props.stagedItems,
    existingEmails: existingEmails.value,
    existingUserNames: existingUserNames.value,
    reservedGroupNames: reservedGroupNames.value,
  });
}

/**
 * Pergunta ao servidor quais e-mails e usuários já existem, e quais turmas já
 * são grupos do titular. Roda ao entrar na conferência e de novo antes de
 * adicionar à lista — entre um momento e outro a pessoa pode ter editado
 * células, e a resposta antiga não vale para o valor novo.
 */
async function runPrecheck(rows) {
  const emails = [...new Set(rows.map((r) => r.email?.trim()).filter(Boolean))];
  const userNames = [...new Set(rows.map((r) => r.userName?.trim()).filter(Boolean))];
  const groupNames = [
    ...new Map(
      rows
        .map((r) => r.groupName)
        .filter(Boolean)
        .map((name) => [groupNameKey(name), name])
    ).values(),
  ];

  if (emails.length === 0 && userNames.length === 0 && groupNames.length === 0) {
    existingEmails.value = new Set();
    existingUserNames.value = new Set();
    existingGroupNames.value = new Set();
    reservedGroupNames.value = new Set();
    return true;
  }

  try {
    const response = await managedAccountService.precheckBatch(
      emails,
      userNames,
      groupNames
    );
    const data = response.data ?? {};
    existingEmails.value = new Set(
      (data.existingEmails ?? []).map((e) => e.trim().toLowerCase())
    );
    existingUserNames.value = new Set(
      (data.existingUserNames ?? []).map((u) => u.trim().toLowerCase())
    );
    existingGroupNames.value = new Set(
      (data.existingGroupNames ?? []).map(groupNameKey)
    );
    reservedGroupNames.value = new Set(
      (data.reservedGroupNames ?? []).map(groupNameKey)
    );
    precheckError.value = "";
    return true;
  } catch (error) {
    // Sem precheck a conferência ainda vale — ela só deixa de apontar colisão
    // com conta existente, que voltaria como erro do lote. Vale seguir com o
    // aviso, e não travar a importação inteira por causa da consulta.
    existingEmails.value = new Set();
    existingUserNames.value = new Set();
    existingGroupNames.value = new Set();
    reservedGroupNames.value = new Set();
    precheckError.value =
      error.response?.data?.message ||
      "Não foi possível conferir quais contas já existem. A importação segue, mas duplicidades só aparecerão no envio.";
    return false;
  }
}

async function goToReview() {
  if (!canReview.value) return;
  isChecking.value = true;
  try {
    const rows = collectRows();
    await runPrecheck(rows);
    analyzedRows.value = analyzeImportRows(rows, {
      stagedItems: props.stagedItems,
      existingEmails: existingEmails.value,
      existingUserNames: existingUserNames.value,
      reservedGroupNames: reservedGroupNames.value,
    });
    overflowAccepted.value = false;
    filter.value = "todas";
    step.value = "conferir";
    emit("expand", true);
  } finally {
    isChecking.value = false;
  }
}

async function recheck() {
  isChecking.value = true;
  try {
    await runPrecheck(analyzedRows.value);
    reanalyze();
  } finally {
    isChecking.value = false;
  }
}

const errorRows = computed(() => analyzedRows.value.filter((r) => r.errors.length > 0));
const readyRows = computed(() => analyzedRows.value.filter((r) => r.errors.length === 0));

const visibleRows = computed(() => {
  if (filter.value === "erro") return errorRows.value;
  if (filter.value === "prontas") return readyRows.value;
  return analyzedRows.value;
});

const overflowCount = computed(() =>
  Math.max(0, readyRows.value.length - props.remainingSlots)
);
const hasOverflow = computed(() => overflowCount.value > 0 && !overflowAccepted.value);

const addableRows = computed(() => {
  if (overflowCount.value > 0) return readyRows.value.slice(0, props.remainingSlots);
  return readyRows.value;
});

// ── Turmas ──
const groupSummary = computed(() =>
  summarizeImportGroups(analyzedRows.value, existingGroupNames.value)
);
const hasAnyGroup = computed(() => analyzedRows.value.some((r) => r.groupName));

// Quantas vagas de "pessoas em grupos" as linhas que vão entrar consomem. Só
// as que vão entrar de fato (addableRows), senão o aviso contaria linha que o
// limite de contas já cortou.
const groupLinksNeeded = computed(
  () => addableRows.value.filter((r) => r.groupName).length
);
const groupOverflowCount = computed(() =>
  Math.max(0, groupLinksNeeded.value - props.remainingGroupSlots)
);
const hasGroupOverflow = computed(() => groupOverflowCount.value > 0);

// Saída do excesso de cota sem perder as contas: as linhas continuam, só a
// turma sai. Quem quiser as turmas amplia o plano e importa de novo.
function clearAllGroups() {
  analyzedRows.value = analyzedRows.value.map((r) => ({ ...r, groupName: "" }));
  reanalyze();
}

function removeRow(id) {
  analyzedRows.value = analyzedRows.value.filter((r) => r.id !== id);
  reanalyze();
}

function removeErrorRows() {
  analyzedRows.value = readyRows.value;
  reanalyze();
}

function trimToRemaining() {
  const keep = new Set(addableRows.value.map((r) => r.id));
  // Mantém as linhas com erro na tela: cortar por excesso não pode esconder o
  // que ainda precisa de correção, senão a pessoa perde o rastro do problema.
  analyzedRows.value = analyzedRows.value.filter(
    (r) => keep.has(r.id) || r.errors.length > 0
  );
  overflowAccepted.value = true;
  reanalyze();
}

function passwordForKind(kindKey) {
  return slots[kindKey].password;
}

async function addToList() {
  if (addableRows.value.length === 0 || hasGroupOverflow.value) return;

  isChecking.value = true;
  try {
    // Reconfere no servidor: entre entrar na tela e clicar aqui, células foram
    // editadas e outra sessão pode ter criado contas.
    await runPrecheck(analyzedRows.value);
    reanalyze();
  } finally {
    isChecking.value = false;
  }

  const rows = addableRows.value;
  if (rows.length === 0) return;

  const items = rows.map((row) =>
    buildImportedItem(row, passwordForKind(row.kind))
  );

  const consumedIds = new Set(rows.map((r) => r.id));
  emit("add", items);

  // O que sobra na tela é só o que ainda não entrou — linha com erro pendente
  // ou cortada pelo limite do plano.
  analyzedRows.value = analyzedRows.value.filter((r) => !consumedIds.has(r.id));

  if (analyzedRows.value.length === 0) {
    resetAll();
  } else {
    overflowAccepted.value = false;
    reanalyze();
  }
}

function resetAll() {
  clearSlot("alunos");
  clearSlot("professores");
  analyzedRows.value = [];
  existingEmails.value = new Set();
  existingUserNames.value = new Set();
  existingGroupNames.value = new Set();
  reservedGroupNames.value = new Set();
  precheckError.value = "";
  filter.value = "todas";
  overflowAccepted.value = false;
  step.value = "escolher";
  emit("expand", false);
}

function backToFiles() {
  step.value = "escolher";
  emit("expand", false);
}

function kindLabel(kindKey) {
  return kinds.find((k) => k.key === kindKey)?.label ?? kindKey;
}
</script>

<template>
  <!-- ── Passo 1: escolher arquivos ───────────────────────────────────────── -->
  <div v-if="step === 'escolher'" class="import-root">
    <p class="import-intro">
      Envie uma planilha para cada grupo. Cada modelo já vem com o perfil certo
      preenchido, e a senha é definida aqui, não existe coluna de senha no
      arquivo. Na planilha de alunos, a coluna <strong>Turma</strong> coloca
      cada aluno no grupo de mesmo nome, criado na hora se ainda não existir.
    </p>

    <div class="kind-grid">
      <div v-for="kind in kinds" :key="kind.key" class="kind-card">
        <div class="kind-head">
          <span class="kind-icon">{{ kind.icon }}</span>
          <div class="kind-titles">
            <span class="kind-title">{{ kind.label }}</span>
            <span class="kind-sub">Perfil padrão: {{ kind.defaultProfile }}</span>
          </div>
        </div>

        <a class="template-link" :href="kind.templateUrl" :download="kind.templateName">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16"
            />
          </svg>
          Baixar modelo (.xlsx)
        </a>

        <label
          class="dropzone"
          :class="{ 'dropzone--filled': slots[kind.key].rows.length > 0 }"
          @dragover.prevent
          @drop.prevent="onDrop(kind.key, $event)"
        >
          <input
            type="file"
            accept=".xlsx"
            class="dropzone-input"
            @change="onPick(kind.key, $event)"
          />
          <span v-if="slots[kind.key].isReading" class="dropzone-text">Lendo…</span>
          <template v-else-if="slots[kind.key].rows.length > 0">
            <span class="dropzone-file" :title="slots[kind.key].file.name">
              ✔ {{ slots[kind.key].file.name }}
            </span>
            <span class="dropzone-count">
              {{ slots[kind.key].rows.length }} linha(s)
              <template v-if="slots[kind.key].skippedRows > 0">
                · {{ slots[kind.key].skippedRows }} em branco ignorada(s)
              </template>
            </span>
          </template>
          <template v-else>
            <span class="dropzone-text">Arraste o arquivo aqui</span>
            <span class="dropzone-hint">ou clique para escolher · .xlsx até 2 MB</span>
          </template>
        </label>

        <button
          v-if="slots[kind.key].rows.length > 0"
          type="button"
          class="btn-clear-slot"
          @click="clearSlot(kind.key)"
        >
          Trocar arquivo
        </button>

        <div v-if="slots[kind.key].rows.length > 0" class="field-group">
          <label class="field-label">
            Senha padrão deste arquivo <span class="required">*</span>
          </label>
          <div class="password-wrapper">
            <input
              v-model="slots[kind.key].password"
              :type="slots[kind.key].showPassword ? 'text' : 'password'"
              class="field-input"
              placeholder="Mín. 8 chars, letra + número"
              autocomplete="new-password"
            />
            <button
              type="button"
              class="password-toggle-btn"
              :aria-label="slots[kind.key].showPassword ? 'Ocultar senha' : 'Exibir senha'"
              @click="slots[kind.key].showPassword = !slots[kind.key].showPassword"
            >
              {{ slots[kind.key].showPassword ? "🙈" : "👁" }}
            </button>
          </div>
          <p class="password-hints">
            Use
            <span :class="{ met: passwordCriteria(slots[kind.key].password).length }"
              >8+ caracteres</span
            >
            com
            <span :class="{ met: passwordCriteria(slots[kind.key].password).letter }"
              >letras</span
            >
            e
            <span :class="{ met: passwordCriteria(slots[kind.key].password).number }"
              >números</span
            >. Vale para as {{ slots[kind.key].rows.length }} contas deste arquivo.
          </p>
        </div>

        <p v-if="slots[kind.key].error" class="slot-error">
          {{ slots[kind.key].error }}
        </p>
      </div>
    </div>

    <div v-if="outdatedTemplateKinds.length > 0" class="notice notice--warn">
      <strong>Modelo desatualizado.</strong>
      {{ outdatedTemplateKinds.map((k) => k.label).join(" e ") }}: a planilha foi
      feita com uma versão anterior do modelo. Ela ainda será lida, mas baixe o
      modelo novo se alguma coluna faltar.
    </div>

    <div class="import-footer">
      <span v-if="totalParsedRows > 0" class="footer-count">
        {{ totalParsedRows }} linha(s) lida(s)
      </span>
      <button type="button" class="btn-primary" :disabled="!canReview" @click="goToReview">
        {{ isChecking ? "Conferindo…" : "Conferir linhas" }}
      </button>
    </div>
  </div>

  <!-- ── Passo 2: conferência ─────────────────────────────────────────────── -->
  <div v-else class="import-root import-root--wide">
    <div class="review-head">
      <div class="review-counts">
        <span class="count count--ok">✔ {{ readyRows.length }} prontas</span>
        <span v-if="errorRows.length > 0" class="count count--err">
          ✖ {{ errorRows.length }} com erro
        </span>
        <span class="count count--muted">{{ remainingSlots }} vaga(s) no plano</span>
      </div>

      <div class="review-tools">
        <div class="filter-tabs" role="tablist">
          <button
            v-for="option in [
              { key: 'todas', label: `Todas (${analyzedRows.length})` },
              { key: 'erro', label: `Com erro (${errorRows.length})` },
              { key: 'prontas', label: `Prontas (${readyRows.length})` },
            ]"
            :key="option.key"
            type="button"
            role="tab"
            :aria-selected="filter === option.key"
            :class="['filter-btn', filter === option.key && 'filter-btn--active']"
            @click="filter = option.key"
          >
            {{ option.label }}
          </button>
        </div>

        <button
          type="button"
          class="btn-ghost-sm"
          :disabled="isChecking"
          @click="recheck"
        >
          {{ isChecking ? "Conferindo…" : "Reconferir" }}
        </button>
        <button
          v-if="errorRows.length > 0"
          type="button"
          class="btn-ghost-sm btn-ghost-sm--danger"
          @click="removeErrorRows"
        >
          Remover as {{ errorRows.length }} com erro
        </button>
      </div>
    </div>

    <div v-if="precheckError" class="notice notice--warn">{{ precheckError }}</div>

    <div v-if="hasOverflow" class="notice notice--block">
      <strong>Mais linhas do que vagas.</strong>
      São {{ readyRows.length }} linhas prontas e restam {{ remainingSlots }} vaga(s)
      no plano. Importe as primeiras {{ remainingSlots }}, remova linhas, ou amplie
      o plano.
      <button type="button" class="btn-inline" @click="trimToRemaining">
        Importar as {{ remainingSlots }} primeiras
      </button>
    </div>

    <div v-if="hasGroupOverflow" class="notice notice--block">
      <strong>Mais turmas do que vagas em grupos.</strong>
      São {{ groupLinksNeeded }} linhas com turma e o plano tem
      {{ remainingGroupSlots }} vaga(s) para pessoas em grupos. Apague a turma de
      algumas linhas, importe sem turma, ou amplie o plano.
      <button type="button" class="btn-inline" @click="clearAllGroups">
        Importar sem turma
      </button>
    </div>

    <!-- Resumo das turmas: "será criada" × "já existe" é o que faz um "9 B"
         com um aluno saltar aos olhos ao lado do "9º B" com trinta. -->
    <div v-if="groupSummary.length > 0" class="group-summary">
      <span class="group-summary-title">
        {{ groupSummary.length }} turma(s)
      </span>
      <span
        v-for="group in groupSummary"
        :key="group.name"
        class="group-chip"
        :class="group.exists ? 'group-chip--existing' : 'group-chip--new'"
        :title="group.exists ? 'Grupo já existe: os alunos entram nele' : 'Grupo será criado na importação'"
      >
        {{ group.name }}
        <span class="group-chip-count">{{ group.count }}</span>
        <span class="group-chip-state">{{ group.exists ? "já existe" : "nova" }}</span>
      </span>
    </div>

    <div class="review-table-wrap">
      <table class="review-table">
        <thead>
          <tr>
            <th class="col-row">Linha</th>
            <th class="col-status"></th>
            <th>Nome</th>
            <th>Usuário</th>
            <th>E-mail</th>
            <th class="col-birth">Nascimento</th>
            <th class="col-age">Idade</th>
            <th>Perfil</th>
            <th v-if="hasAnyGroup">Turma</th>
            <th class="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in visibleRows" :key="row.id">
            <tr :class="row.errors.length > 0 ? 'row--error' : 'row--ok'">
              <td class="col-row">
                <span :title="`${kindLabel(row.kind)} · ${row.sourceFile}`">
                  {{ row.rowNumber }}
                </span>
              </td>
              <td class="col-status">{{ row.errors.length > 0 ? "✖" : "✔" }}</td>

              <template v-if="row.errors.length > 0">
                <td><input v-model="row.fullName" class="cell-input" @change="reanalyze" /></td>
                <td><input v-model="row.userName" class="cell-input" @change="reanalyze" /></td>
                <td><input v-model="row.email" class="cell-input" @change="reanalyze" /></td>
                <td class="col-birth">
                  <input
                    v-model="row.birthDate"
                    type="date"
                    class="cell-input"
                    @change="reanalyze"
                  />
                </td>
                <td class="col-age">{{ row.age ?? "—" }}</td>
                <td>
                  <input
                    v-model="row.relationshipType"
                    class="cell-input"
                    @change="reanalyze"
                  />
                </td>
                <td v-if="hasAnyGroup">
                  <input
                    v-if="getImportKind(row.kind).hasGroupColumn"
                    v-model="row.groupName"
                    class="cell-input"
                    placeholder="sem turma"
                    @change="reanalyze"
                  />
                  <span v-else class="cell-muted">—</span>
                </td>
              </template>
              <template v-else>
                <td class="cell-text">{{ row.fullName || "—" }}</td>
                <td class="cell-text">{{ row.userName }}</td>
                <td class="cell-text">{{ row.email }}</td>
                <td class="cell-text col-birth">{{ row.birthDate }}</td>
                <td class="col-age">
                  {{ row.age }}<span v-if="row.age !== null && row.age < 18"> 👶</span>
                </td>
                <td class="cell-text">
                  <span class="profile-chip">{{ row.relationshipType }}</span>
                </td>
                <td v-if="hasAnyGroup" class="cell-text">
                  <span v-if="row.groupName" class="profile-chip profile-chip--group">
                    {{ row.groupName }}
                  </span>
                  <span v-else class="cell-muted">—</span>
                </td>
              </template>

              <td class="col-actions">
                <button
                  type="button"
                  class="btn-remove-row"
                  title="Remover esta linha"
                  @click="removeRow(row.id)"
                >
                  ✕
                </button>
              </td>
            </tr>
            <tr v-if="row.errors.length > 0" class="row--detail">
              <td :colspan="hasAnyGroup ? 10 : 9">
                <span v-for="(message, i) in row.errors" :key="i" class="row-error">
                  ↳ {{ message }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <p v-if="visibleRows.length === 0" class="review-empty">
        Nenhuma linha neste filtro.
      </p>
    </div>

    <p class="password-note">
      🔑 As contas recebem a senha padrão informada por arquivo. Cada pessoa pode
      trocá-la depois em “Esqueci minha senha”.
    </p>

    <div class="import-footer">
      <button type="button" class="btn-skip" @click="backToFiles">Voltar</button>
      <button type="button" class="btn-skip" @click="resetAll">Descartar importação</button>
      <button
        type="button"
        class="btn-primary"
        :disabled="addableRows.length === 0 || hasOverflow || hasGroupOverflow || isChecking"
        @click="addToList"
      >
        {{ isChecking ? "Conferindo…" : `Adicionar ${addableRows.length} à lista` }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.import-root {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}
.import-intro {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.45;
}

/* ── Passo 1 ─────────────────────────────────────────────────────────────── */
.kind-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  /* start, e não o stretch padrão: sem isso os dois cartões esticam até o fim
     do painel de 710px e a dropzone fica boiando num retângulo vazio. */
  align-content: start;
  align-items: start;
  gap: 0.6rem;
  overflow-y: auto;
  min-height: 0;
  flex: 1;
  padding-right: 0.15rem;
}
.kind-card {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  padding: 0.6rem;
  background: var(--surface);

  /* Altura fixa, medida no estado mais alto: o de arquivo escolhido, que
     acrescenta nome do arquivo, "Trocar arquivo" e o bloco inteiro da senha.
     Esse estado fecha em 266px (igual com 184 ou 1000 contas), e o de erro em
     212px — os dois nunca coexistem, porque handleFile zera file/rows ao
     falhar. 18rem = 288px deixa 22px de folga para a dica da senha quebrar
     numa linha a mais sem estourar o cartão.

     Sem isso o cartão saltava de 175px para 266px ao escolher o arquivo, e o
     cartão ainda vazio ao lado ficava desalinhado. Quem absorve a diferença é
     a dropzone (flex: 1 abaixo): ela vai de 94px no estado preenchido a 203px
     no vazio, ocupando de antemão o espaço dos blocos que ainda não existem —
     o que de quebra dá um alvo de arrastar bem maior justamente quando é a
     única coisa no cartão. */
  height: 18rem;
}
.kind-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.kind-icon {
  font-size: 1.15rem;
  line-height: 1;
}
.kind-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.kind-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-dark);
}
.kind-sub {
  font-size: 0.7rem;
  color: var(--text-muted);
}
.template-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--brand);
  text-decoration: none;
}
.template-link:hover {
  text-decoration: underline;
}
.template-link svg {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}
.dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  /* A parte elástica do cartão de altura fixa: absorve o espaço dos blocos que
     só existem depois do arquivo escolhido. O min-height é o piso do estado
     preenchido, quando a senha já tomou o resto. */
  flex: 1;
  min-height: 72px;
  padding: 0.5rem;
  border: 1px dashed var(--neutral-400);
  border-radius: 9px;
  background: #fafbfc;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s, background 0.2s;
}
.dropzone:hover {
  border-color: var(--brand-light);
  background: #f8f6ff;
}
.dropzone--filled {
  border-style: solid;
  border-color: #86efac;
  background: #f0fdf4;
}
.dropzone-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.dropzone-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-light);
}
.dropzone-hint {
  font-size: 0.68rem;
  color: var(--text-subtle);
}
.dropzone-file {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--success-text);
  word-break: break-all;
  /* Duas linhas no máximo: o nome do arquivo é o único conteúdo de tamanho
     imprevisível do cartão, e num cartão de altura fixa um nome quilométrico
     transbordaria. O nome inteiro fica no title. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dropzone-count {
  font-size: 0.68rem;
  color: var(--success-text);
  opacity: 0.85;
}
.btn-clear-slot {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  text-decoration: underline;
}
.btn-clear-slot:hover {
  color: var(--text-dark);
}
.slot-error {
  font-size: 0.72rem;
  color: var(--danger-text);
  margin: 0;
  line-height: 1.4;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.field-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-dark);
}
.required {
  color: var(--danger);
}
.field-input {
  height: 30px;
  padding: 0 0.6rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  font-size: 0.82rem;
  color: var(--text-dark);
  background: var(--surface);
  width: 100%;
}
.field-input:focus {
  outline: none;
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-brand);
}
.password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.password-wrapper .field-input {
  padding-right: 2.2rem;
}
.password-toggle-btn {
  position: absolute;
  right: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  line-height: 1;
  padding: 0.1rem;
}
.password-hints {
  font-size: 0.68rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
}
.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

/* ── Passo 2 ─────────────────────────────────────────────────────────────── */
.import-root--wide {
  gap: 0.5rem;
}
.review-head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex-shrink: 0;
}
.review-counts {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.count {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
}
.count--ok {
  background: var(--success-bg);
  color: var(--success-text);
}
.count--err {
  background: var(--danger-bg);
  color: var(--danger-text);
}
.count--muted {
  background: var(--neutral-200);
  color: var(--text-light);
}
.review-tools {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.filter-tabs {
  display: inline-flex;
  gap: 0.15rem;
  padding: 0.15rem;
  border: 1px solid var(--neutral-200);
  border-radius: 999px;
  background: var(--surface-3);
}
.filter-btn {
  height: 24px;
  padding: 0 0.6rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.filter-btn:hover {
  background: var(--neutral-200);
}
.filter-btn--active {
  background: #d8d0ff;
  color: var(--text-on-brand);
}
.btn-ghost-sm {
  height: 26px;
  padding: 0 0.65rem;
  border: 1px solid var(--neutral-400);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text-light);
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-ghost-sm:hover:not(:disabled) {
  background: var(--surface-3);
}
.btn-ghost-sm:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-ghost-sm--danger {
  border-color: var(--danger);
  color: var(--danger-text);
}
.btn-ghost-sm--danger:hover {
  background: var(--danger-bg);
}

.review-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--neutral-200);
  border-radius: 9px;
  background: var(--surface);
}
.review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.review-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--surface-2);
  color: var(--text-light);
  font-weight: 700;
  text-align: left;
  padding: 0.35rem 0.45rem;
  border-bottom: 1px solid var(--neutral-200);
  white-space: nowrap;
}
.review-table td {
  padding: 0.25rem 0.45rem;
  border-bottom: 1px solid var(--surface-3);
  vertical-align: middle;
}
.row--ok td {
  color: var(--text-dark);
}
.row--error td {
  background: var(--danger-bg);
}
.row--detail td {
  background: var(--danger-bg);
  padding-top: 0;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid var(--danger);
}
.row-error {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--danger-text);
  padding-left: 3.2rem;
}
.cell-text {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cell-input {
  width: 100%;
  min-width: 90px;
  height: 24px;
  padding: 0 0.35rem;
  border: 1px solid var(--danger);
  border-radius: 5px;
  font-family: inherit;
  font-size: 0.73rem;
  color: var(--text-dark);
  background: var(--surface);
}
.cell-input:focus {
  outline: none;
  border-color: var(--brand-light);
  box-shadow: 0 0 0 2px rgba(189, 178, 255, 0.25);
}
.col-row,
.col-age {
  width: 1%;
  white-space: nowrap;
  color: var(--text-muted);
}
.col-status {
  width: 1%;
  text-align: center;
}
.col-birth {
  width: 120px;
}
.col-actions {
  width: 1%;
}
.profile-chip {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--brand);
  background: var(--brand-tint);
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  white-space: nowrap;
}
.profile-chip--group {
  color: #0f766e;
  background: #ccfbf1;
}
.cell-muted {
  color: var(--neutral-400);
}

/* Resumo das turmas acima da tabela */
.group-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.6rem;
}
.group-summary-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-light);
  margin-right: 0.2rem;
}
.group-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}
.group-chip--existing {
  color: #0f766e;
  background: #ccfbf1;
  border-color: #99f6e4;
}
.group-chip--new {
  color: #4338ca;
  background: var(--brand-tint);
  border-color: #c7d2fe;
}
.group-chip-count {
  font-weight: 700;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
}
.group-chip-state {
  font-weight: 500;
  opacity: 0.8;
}
.btn-remove-row {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-subtle);
  font-size: 0.8rem;
  line-height: 1;
  padding: 0.15rem 0.25rem;
}
.btn-remove-row:hover {
  color: var(--danger);
}
.review-empty {
  font-size: 0.78rem;
  color: var(--text-subtle);
  text-align: center;
  font-style: italic;
  padding: 1rem;
  margin: 0;
}
.password-note {
  font-size: 0.7rem;
  color: var(--text-muted);
  margin: 0;
  flex-shrink: 0;
}

/* ── Avisos e rodapé ─────────────────────────────────────────────────────── */
.notice {
  border-radius: 8px;
  padding: 0.45rem 0.65rem;
  font-size: 0.73rem;
  line-height: 1.45;
  flex-shrink: 0;
}
.notice--warn {
  background: var(--warning-bg);
  border: 1px solid #fde047;
  color: #854d0e;
}
.notice--block {
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  color: var(--danger-text);
}
.btn-inline {
  margin-left: 0.35rem;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.73rem;
  font-weight: 700;
  color: var(--danger-text);
  cursor: pointer;
  text-decoration: underline;
}
.import-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.35rem;
}
.footer-count {
  margin-right: auto;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.btn-skip {
  padding: 0.5rem 0.9rem;
  border-radius: 9px;
  border: 1px solid var(--neutral-400);
  background-color: var(--neutral-200);
  color: var(--text-dark);
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.btn-skip:hover {
  background-color: var(--neutral-300);
}
</style>
